import { useState } from 'react';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, FileText, Shield, HelpCircle, ChevronDown, ChevronUp, Globe, Plane, CreditCard, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const PracticalInfoPage = () => {
  const { t, language } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = {
    fr: [
      {
        question: "Comment prendre rendez-vous avec un praticien à l'étranger ?",
        answer: "Vous pouvez rechercher un praticien dans le pays de votre choix en utilisant nos filtres de recherche. Une fois que vous avez trouvé un praticien, consultez ses disponibilités et réservez directement en ligne. Vous recevrez une confirmation par email."
      },
      {
        question: "Les consultations en visio sont-elles disponibles ?",
        answer: "Oui, de nombreux praticiens proposent des consultations en visioconférence. Recherchez les praticiens avec l'option 'Visio' activée. C'est idéal pour les patients internationaux ou ceux qui ne peuvent pas se déplacer."
      },
      {
        question: "Comment fonctionne le paiement pour les patients internationaux ?",
        answer: "Les paiements sont sécurisés via Stripe et acceptent les principales cartes bancaires internationales. Les tarifs sont affichés en euros. Votre banque peut appliquer des frais de change."
      },
      {
        question: "Ai-je besoin d'un visa pour consulter un praticien en Europe ?",
        answer: "Cela dépend de votre nationalité et de la durée de votre séjour. Pour les soins médicaux courts, un visa touristique suffit généralement. Pour des traitements plus longs, consultez l'ambassade du pays concerné."
      },
      {
        question: "Les praticiens parlent-ils plusieurs langues ?",
        answer: "Beaucoup de nos praticiens sont multilingues. Vous pouvez voir les langues parlées sur chaque profil de praticien. Utilisez le filtre de langue pour trouver un praticien qui parle votre langue."
      },
      {
        question: "Mon assurance santé couvre-t-elle les soins à l'étranger ?",
        answer: "Cela dépend de votre contrat d'assurance. Nous vous recommandons de contacter votre assureur avant votre consultation. Certaines mutuelles internationales couvrent les médecines alternatives."
      }
    ],
    en: [
      {
        question: "How can I book an appointment with a practitioner abroad?",
        answer: "You can search for a practitioner in your country of choice using our search filters. Once you've found a practitioner, check their availability and book directly online. You will receive a confirmation email."
      },
      {
        question: "Are video consultations available?",
        answer: "Yes, many practitioners offer video consultations. Look for practitioners with the 'Video' option enabled. It's ideal for international patients or those who cannot travel."
      },
      {
        question: "How does payment work for international patients?",
        answer: "Payments are secured via Stripe and accept major international credit cards. Prices are displayed in euros. Your bank may apply exchange fees."
      },
      {
        question: "Do I need a visa to consult a practitioner in Europe?",
        answer: "This depends on your nationality and length of stay. For short medical care, a tourist visa is usually sufficient. For longer treatments, consult the embassy of the country concerned."
      },
      {
        question: "Do practitioners speak multiple languages?",
        answer: "Many of our practitioners are multilingual. You can see the languages spoken on each practitioner's profile. Use the language filter to find a practitioner who speaks your language."
      },
      {
        question: "Does my health insurance cover care abroad?",
        answer: "This depends on your insurance contract. We recommend contacting your insurer before your consultation. Some international health plans cover alternative medicine."
      }
    ],
    uk: [
      {
        question: "Як записатися на прийом до спеціаліста за кордоном?",
        answer: "Ви можете знайти спеціаліста в обраній країні за допомогою наших фільтрів пошуку. Після того, як ви знайшли спеціаліста, перевірте його наявність та забронюйте онлайн. Ви отримаєте підтвердження електронною поштою."
      },
      {
        question: "Чи доступні відео-консультації?",
        answer: "Так, багато спеціалістів пропонують відео-консультації. Шукайте спеціалістів з опцією 'Відео'. Це ідеально для міжнародних пацієнтів або тих, хто не може подорожувати."
      },
      {
        question: "Як працює оплата для міжнародних пацієнтів?",
        answer: "Платежі захищені через Stripe і приймають основні міжнародні кредитні картки. Ціни відображаються в євро. Ваш банк може застосовувати курсові комісії."
      },
      {
        question: "Чи потрібна мені віза для консультації зі спеціалістом в Європі?",
        answer: "Це залежить від вашого громадянства та тривалості перебування. Для короткочасної медичної допомоги зазвичай достатньо туристичної візи. Для тривалого лікування зверніться до посольства відповідної країни."
      },
      {
        question: "Чи розмовляють спеціалісти кількома мовами?",
        answer: "Багато наших спеціалістів багатомовні. Ви можете побачити мови на профілі кожного спеціаліста. Використовуйте фільтр мови, щоб знайти спеціаліста, який розмовляє вашою мовою."
      },
      {
        question: "Чи покриває моя медична страховка лікування за кордоном?",
        answer: "Це залежить від вашого страхового договору. Рекомендуємо зв'язатися з вашим страховиком перед консультацією. Деякі міжнародні плани охоплюють альтернативну медицину."
      }
    ],
    ru: [
      {
        question: "Как записаться на прием к специалисту за рубежом?",
        answer: "Вы можете найти специалиста в выбранной стране с помощью наших фильтров поиска. Найдя специалиста, проверьте его доступность и забронируйте онлайн. Вы получите подтверждение по электронной почте."
      },
      {
        question: "Доступны ли видео-консультации?",
        answer: "Да, многие специалисты предлагают видео-консультации. Ищите специалистов с опцией 'Видео'. Это идеально для международных пациентов или тех, кто не может путешествовать."
      },
      {
        question: "Как работает оплата для международных пациентов?",
        answer: "Платежи защищены через Stripe и принимают основные международные кредитные карты. Цены отображаются в евро. Ваш банк может применять курсовые комиссии."
      },
      {
        question: "Нужна ли мне виза для консультации со специалистом в Европе?",
        answer: "Это зависит от вашего гражданства и продолжительности пребывания. Для краткосрочной медицинской помощи обычно достаточно туристической визы. Для длительного лечения обратитесь в посольство соответствующей страны."
      },
      {
        question: "Специалисты говорят на нескольких языках?",
        answer: "Многие наши специалисты многоязычны. Вы можете увидеть языки в профиле каждого специалиста. Используйте фильтр языка, чтобы найти специалиста, говорящего на вашем языке."
      },
      {
        question: "Покрывает ли моя медицинская страховка лечение за рубежом?",
        answer: "Это зависит от вашего страхового договора. Рекомендуем связаться со страховщиком перед консультацией. Некоторые международные планы покрывают альтернативную медицину."
      }
    ],
    it: [
      {
        question: "Come posso prenotare un appuntamento con un professionista all'estero?",
        answer: "Puoi cercare un professionista nel paese di tua scelta utilizzando i nostri filtri di ricerca. Una volta trovato, controlla la sua disponibilità e prenota direttamente online. Riceverai una conferma via email."
      },
      {
        question: "Le consulenze video sono disponibili?",
        answer: "Sì, molti professionisti offrono consulenze video. Cerca i professionisti con l'opzione 'Video' attivata. È ideale per i pazienti internazionali o per chi non può spostarsi."
      },
      {
        question: "Come funziona il pagamento per i pazienti internazionali?",
        answer: "I pagamenti sono sicuri tramite Stripe e accettano le principali carte di credito internazionali. I prezzi sono indicati in euro. La tua banca potrebbe applicare commissioni di cambio."
      },
      {
        question: "Ho bisogno di un visto per consultare un professionista in Europa?",
        answer: "Dipende dalla tua nazionalità e dalla durata del soggiorno. Per cure mediche brevi, un visto turistico è generalmente sufficiente. Per trattamenti più lunghi, consulta l'ambasciata del paese interessato."
      },
      {
        question: "I professionisti parlano più lingue?",
        answer: "Molti dei nostri professionisti sono multilingue. Puoi vedere le lingue parlate sul profilo di ogni professionista. Usa il filtro della lingua per trovare un professionista che parla la tua lingua."
      },
      {
        question: "La mia assicurazione sanitaria copre le cure all'estero?",
        answer: "Dipende dal tuo contratto assicurativo. Ti consigliamo di contattare il tuo assicuratore prima della consulenza. Alcune assicurazioni internazionali coprono le medicine alternative."
      }
    ],
    ro: [
      {
        question: "Cum pot programa o întâlnire cu un specialist în străinătate?",
        answer: "Poți căuta un specialist în țara dorită folosind filtrele noastre de căutare. După ce ai găsit un specialist, verifică disponibilitatea și rezervă direct online. Vei primi o confirmare pe email."
      },
      {
        question: "Sunt disponibile consultațiile video?",
        answer: "Da, mulți specialiști oferă consultații video. Caută specialiștii cu opțiunea 'Video' activată. Este ideal pentru pacienții internaționali sau pentru cei care nu pot călători."
      },
      {
        question: "Cum funcționează plata pentru pacienții internaționali?",
        answer: "Plățile sunt securizate prin Stripe și acceptă principalele carduri de credit internaționale. Prețurile sunt afișate în euro. Banca ta poate aplica comisioane de schimb valutar."
      },
      {
        question: "Am nevoie de viză pentru a consulta un specialist în Europa?",
        answer: "Depinde de naționalitatea ta și de durata șederii. Pentru îngrijiri medicale scurte, o viză turistică este de obicei suficientă. Pentru tratamente mai lungi, consultă ambasada țării respective."
      },
      {
        question: "Specialiștii vorbesc mai multe limbi?",
        answer: "Mulți dintre specialiștii noștri sunt multilingvi. Poți vedea limbile vorbite pe profilul fiecărui specialist. Folosește filtrul de limbă pentru a găsi un specialist care vorbește limba ta."
      },
      {
        question: "Asigurarea mea de sănătate acoperă îngrijirea în străinătate?",
        answer: "Depinde de contractul tău de asigurare. Îți recomandăm să contactezi asigurătorul înainte de consultație. Unele asigurări internaționale acoperă medicina alternativă."
      }
    ],
    ka: [
      {
        question: "როგორ დავჯავშნო შეხვედრა საზღვარგარეთ მყოფ სპეციალისტთან?",
        answer: "შეგიძლიათ მოძებნოთ სპეციალისტი თქვენთვის სასურველ ქვეყანაში ჩვენი ძიების ფილტრების გამოყენებით. სპეციალისტის პოვნის შემდეგ შეამოწმეთ მისი ხელმისაწვდომობა და დაჯავშნეთ პირდაპირ ონლაინ. მიიღებთ დადასტურებას ელფოსტით."
      },
      {
        question: "ხელმისაწვდომია თუ არა ვიდეო კონსულტაციები?",
        answer: "დიახ, ბევრი სპეციალისტი გთავაზობთ ვიდეო კონსულტაციებს. მოძებნეთ სპეციალისტები 'ვიდეო' ოფციით. ეს იდეალურია საერთაშორისო პაციენტებისთვის ან მათთვის, ვინც ვერ მგზავრობს."
      },
      {
        question: "როგორ მუშაობს გადახდა საერთაშორისო პაციენტებისთვის?",
        answer: "გადახდები დაცულია Stripe-ის მეშვეობით და მიიღება ძირითადი საერთაშორისო საკრედიტო ბარათები. ფასები ნაჩვენებია ევროში. თქვენმა ბანკმა შეიძლება დააწესოს კონვერტაციის საკომისიო."
      },
      {
        question: "მჭირდება თუ არა ვიზა ევროპაში სპეციალისტთან კონსულტაციისთვის?",
        answer: "ეს დამოკიდებულია თქვენს მოქალაქეობაზე და ყოფნის ხანგრძლივობაზე. მოკლევადიანი სამედიცინო მომსახურებისთვის ჩვეულებრივ საკმარისია ტურისტული ვიზა. გრძელვადიანი მკურნალობისთვის მიმართეთ შესაბამისი ქვეყნის საელჩოს."
      },
      {
        question: "სპეციალისტები საუბრობენ რამდენიმე ენაზე?",
        answer: "ჩვენი სპეციალისტების უმრავლესობა მრავალენოვანია. თქვენ შეგიძლიათ ნახოთ საუბრის ენები თითოეული სპეციალისტის პროფილში. გამოიყენეთ ენის ფილტრი თქვენს ენაზე მოსაუბრე სპეციალისტის მოსაძებნად."
      },
      {
        question: "ჩემი ჯანმრთელობის დაზღვევა ფარავს საზღვარგარეთ მკურნალობას?",
        answer: "ეს დამოკიდებულია თქვენს სადაზღვევო ხელშეკრულებაზე. გირჩევთ დაუკავშირდეთ თქვენს დამზღვევს კონსულტაციამდე. ზოგიერთი საერთაშორისო გეგმა ფარავს ალტერნატიულ მედიცინას."
      }
    ]
  };

  const currentFaqs = faqs[language] || faqs['fr'];

  const infoCards = [
    {
      icon: Globe,
      title: language === 'ka' ? 'საერთაშორისო წვდომა' : language === 'it' ? 'Accesso internazionale' : language === 'ro' ? 'Acces internațional' : language === 'uk' ? 'Міжнародний доступ' : language === 'ru' ? 'Международный доступ' : language === 'en' ? 'International Access' : 'Accès international',
      description: language === 'ka' ? 'იპოვეთ სპეციალისტები მთელ მსოფლიოში' : language === 'it' ? 'Trova professionisti in tutto il mondo' : language === 'ro' ? 'Găsește specialiști în toată lumea' : language === 'uk' ? 'Знайдіть спеціалістів по всьому світу' : language === 'ru' ? 'Найдите специалистов по всему миру' : language === 'en' ? 'Find practitioners worldwide' : 'Trouvez des praticiens dans le monde entier',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      icon: Plane,
      title: language === 'ka' ? 'ვიზა და მოგზაურობა' : language === 'it' ? 'Visto e viaggi' : language === 'ro' ? 'Viză și călătorii' : language === 'uk' ? 'Віза та поїздки' : language === 'ru' ? 'Виза и поездки' : language === 'en' ? 'Visa & Travel' : 'Visa & Voyages',
      description: language === 'ka' ? 'ინფორმაცია სხვადასხვა ქვეყანაში შესვლის შესახებ' : language === 'it' ? "Informazioni sull'ingresso nei diversi paesi" : language === 'ro' ? 'Informații despre intrarea în diferite țări' : language === 'uk' ? 'Інформація про в\'їзд до різних країн' : language === 'ru' ? 'Информация о въезде в разные страны' : language === 'en' ? 'Entry information for different countries' : "Informations sur l'entrée dans différents pays",
      color: 'bg-amber-50 text-amber-600'
    },
    {
      icon: Shield,
      title: language === 'ka' ? 'ჯანმრთელობის დაზღვევა' : language === 'it' ? 'Assicurazione sanitaria' : language === 'ro' ? 'Asigurare de sănătate' : language === 'uk' ? 'Страхування здоров\'я' : language === 'ru' ? 'Страхование здоровья' : language === 'en' ? 'Health Insurance' : 'Assurance santé',
      description: language === 'ka' ? 'რჩევები საერთაშორისო დაზღვევის შესახებ' : language === 'it' ? 'Consigli per le assicurazioni internazionali' : language === 'ro' ? 'Sfaturi pentru asigurări internaționale' : language === 'uk' ? 'Поради щодо міжнародного страхування' : language === 'ru' ? 'Советы по международному страхованию' : language === 'en' ? 'International insurance advice' : 'Conseils pour les assurances internationales',
      color: 'bg-green-50 text-green-600'
    },
    {
      icon: CreditCard,
      title: language === 'ka' ? 'უსაფრთხო გადახდა' : language === 'it' ? 'Pagamento sicuro' : language === 'ro' ? 'Plată securizată' : language === 'uk' ? 'Безпечна оплата' : language === 'ru' ? 'Безопасная оплата' : language === 'en' ? 'Secure Payment' : 'Paiement sécurisé',
      description: language === 'ka' ? 'გადახდები დაცული და მიღებულია ყველგან' : language === 'it' ? 'Pagamenti sicuri e accettati ovunque' : language === 'ro' ? 'Plăți securizate și acceptate peste tot' : language === 'uk' ? 'Платежі захищені та прийнятні у всьому світі' : language === 'ru' ? 'Платежи защищены и принимаются по всему миру' : language === 'en' ? 'Payments secured and accepted worldwide' : 'Paiements sécurisés et acceptés partout',
      color: 'bg-purple-50 text-purple-600'
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="practical-info-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-sage-50 to-sand-50 py-16">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              {language === 'ka' ? 'პრაქტიკული ინფორმაცია' : language === 'it' ? 'Informazioni pratiche' : language === 'ro' ? 'Informații practice' : language === 'uk' ? 'Практична інформація' : language === 'ru' ? 'Практическая информация' : language === 'en' ? 'Practical Information' : 'Informations pratiques'}
            </h1>
            <p className="text-lg text-muted-foreground">
              {language === 'ka' ? 'ყველაფერი რაც უნდა იცოდეთ საზღვარგარეთ სპეციალისტთან კონსულტაციისთვის' : language === 'it' ? "Tutto ciò che devi sapere per consultare un professionista all'estero" : language === 'ro' ? 'Tot ce trebuie să știi pentru a consulta un specialist în străinătate' : language === 'uk' ? 'Все, що вам потрібно знати для консультації зі спеціалістом за кордоном' : language === 'ru' ? 'Всё, что вам нужно знать для консультации со специалистом за рубежом' : language === 'en' ? 'Everything you need to know to consult a practitioner abroad' : 'Tout ce que vous devez savoir pour consulter un praticien à l\'étranger'}
            </p>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="container-app py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {infoCards.map((card, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 border border-sand-200 shadow-soft hover:shadow-md transition-shadow">
              <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center mb-4`}>
                <card.icon className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-sm text-muted-foreground">{card.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container-app py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <HelpCircle className="w-12 h-12 text-sage-500 mx-auto mb-4" />
            <h2 className="font-heading text-3xl font-bold mb-3">
              {language === 'ka' ? 'ხშირად დასმული კითხვები' : language === 'it' ? 'Domande frequenti' : language === 'ro' ? 'Întrebări frecvente' : language === 'uk' ? 'Часті запитання' : language === 'ru' ? 'Часто задаваемые вопросы' : language === 'en' ? 'Frequently Asked Questions' : 'Questions fréquentes'}
            </h2>
            <p className="text-muted-foreground">
              {language === 'ka' ? 'პასუხები საერთაშორისო პაციენტების კითხვებზე' : language === 'it' ? 'Risposte alle domande dei pazienti internazionali' : language === 'ro' ? 'Răspunsuri la întrebările pacienților internaționali' : language === 'uk' ? 'Відповіді на питання міжнародних пацієнтів' : language === 'ru' ? 'Ответы на вопросы международных пациентов' : language === 'en' ? 'Answers to international patient questions' : 'Réponses aux questions des patients internationaux'}
            </p>
          </div>

          <div className="space-y-4">
            {currentFaqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-sand-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-sand-50 transition-colors"
                >
                  <span className="font-medium text-foreground pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-sage-500 shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-muted-foreground animate-fadeIn">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="bg-sage-500 py-12">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            {language === 'ka' ? 'კიდევ გაქვთ კითხვები?' : language === 'it' ? 'Hai altre domande?' : language === 'ro' ? 'Ai alte întrebări?' : language === 'uk' ? 'Залишилися питання?' : language === 'ru' ? 'Остались вопросы?' : language === 'en' ? 'Still have questions?' : 'Vous avez d\'autres questions ?'}
          </h2>
          <p className="text-sage-100 mb-6">
            {language === 'ka' ? 'დაუკავშირდით ჩვენს საერთაშორისო მხარდაჭერის გუნდს' : language === 'it' ? 'Contatta il nostro team di supporto internazionale' : language === 'ro' ? 'Contactează echipa noastră de suport internațional' : language === 'uk' ? 'Зв\'яжіться з нашою міжнародною командою підтримки' : language === 'ru' ? 'Свяжитесь с нашей международной командой поддержки' : language === 'en' ? 'Contact our international support team' : 'Contactez notre équipe de support internationale'}
          </p>
          <Button 
            asChild
            className="bg-white text-sage-600 hover:bg-sand-50"
          >
            <a href="mailto:contact@apora.net">contact@apora.net</a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PracticalInfoPage;
